import {
  ReactNode,
  ReactElement,
  Children,
  useState,
  MutableRefObject,
} from 'react';
import { Button, ButtonType, DangerButton, PrimaryButton } from '@ardoq/button';
import { Checkbox, TextInput } from '@ardoq/forms';
import { DoqFace, DoqSize, DoqType } from '@ardoq/doq';
import { ModalContainer, ModalContainerProps } from './ModalContainer';
import { ModalHeader } from './ModalHeader';
import { ModalFooter } from './ModalFooter';
import { ModalSubtitle } from './ModalSubtitle';
import { ModalText } from './ModalText';
import { ModalList } from './ModalList';
import { ModalListOption, ModalSize } from '../types';
import { ButtonContainer, DoqWrapper, ModalLayout } from './atoms';
import { ModalLoader } from './ModalLoader';
import {
  ErrorNotification,
  InfoNotification,
  WarningNotification,
} from '@ardoq/status-ui';
import { Paragraph, Text } from '@ardoq/typography';
import { ModalWrapper } from './ModalWrapper';
import { DoqAndTextPositioner } from './DoqAndTextPositioner';
import { Stack } from '@ardoq/layout';

export type ModalTemplateProps = {
  headerText?: ReactNode;
  /** @deprecated please use text for all the text content instead */
  subtitle?: string;
  text?: ReactNode;
  /** @deprecated please use text for all the text content instead */
  text2?: ReactNode;
  /** @deprecated please use children instead */
  formContent?: ReactNode;
  /** @deprecated the footer should be used only for the modal buttons */
  footerContent?: ReactNode;
  inputLabel?: string;
  inputValue?: string;
  onInputValueChange?: (inputValue: string) => void;
  showRememberActionCheckbox?: boolean;
  rememberActionCustomCheckboxText?: string;
  onRememberActionCheckboxChange?: (
    rememberActionCheckboxState: boolean
  ) => void;
  rememberActionCheckboxState?: boolean;
  errorBlockMessage?: ReactNode;
  warningBlockMessage?: ReactNode;
  infoNotification?: ReactNode;
  secondaryButtonText?: string;
  secondaryButtonClickId?: string;
  secondaryButtonDataTestId?: string;
  isSecondaryButtonDisabled?: boolean;
  onSecondaryButtonClick?: () => void;
  primaryButtonText?: string;
  primaryButtonClickId?: string;
  primaryButtonDataTestId?: string;
  isPrimaryButtonDisabled?: boolean;
  onPrimaryButtonClick?: () => void;
  primaryButtonRef?: MutableRefObject<HTMLButtonElement | null>;
  dangerButtonText?: string;
  dangerButtonClickId?: string;
  dangerButtonDataTestId?: string;
  isDangerButtonDisabled?: boolean;
  onDangerButtonClick?: () => void;
  /** @deprecated please use <ModalList/> directly in the children prop instead */
  listOptions?: ModalListOption[];
  children?: ReactNode;
  onCloseButtonClick?: () => void;
  onInputEnterPress?: () => void;
  doqType?: DoqType;
  textInputId?: string;
  textInputRef?: MutableRefObject<HTMLInputElement | null>;
  inputPlaceHolderText?: string;
  notification?: ReactElement;
  isInProgress?: boolean;
  isLoading?: boolean;
  inputDataTestId?: string;
  closeButtonLabel?: string;
} & Pick<ModalContainerProps, 'fixedHeight' | 'modalSize'>;

const ModalTemplate = ({
  modalSize,
  fixedHeight,
  onCloseButtonClick,
  headerText,
  subtitle,
  text,
  text2,
  inputLabel,
  rememberActionCustomCheckboxText,
  showRememberActionCheckbox,
  onRememberActionCheckboxChange,
  rememberActionCheckboxState,
  errorBlockMessage,
  warningBlockMessage,
  infoNotification,
  listOptions = [],
  formContent,
  footerContent,
  children,
  secondaryButtonText,
  secondaryButtonClickId,
  secondaryButtonDataTestId,
  isSecondaryButtonDisabled,
  onSecondaryButtonClick,
  primaryButtonText,
  primaryButtonClickId,
  primaryButtonDataTestId,
  isPrimaryButtonDisabled,
  onPrimaryButtonClick,
  dangerButtonText,
  dangerButtonClickId,
  dangerButtonDataTestId,
  isDangerButtonDisabled,
  onDangerButtonClick,
  inputValue,
  onInputValueChange,
  doqType,
  onInputEnterPress,
  textInputId,
  inputPlaceHolderText,
  notification,
  isInProgress,
  isLoading,
  primaryButtonRef,
  textInputRef,
  inputDataTestId,
  closeButtonLabel,
}: ModalTemplateProps) => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const shouldRenderHeader = headerText || onCloseButtonClick;
  const shouldRenderFooter =
    footerContent ||
    showRememberActionCheckbox ||
    secondaryButtonText ||
    dangerButtonText ||
    primaryButtonText;
  const shouldRenderTextSection =
    subtitle || text || text2 || Boolean(listOptions.length);
  const shouldRenderInputSection =
    inputLabel || onInputValueChange || formContent;
  const shouldRenderNotificationSection =
    errorBlockMessage ||
    warningBlockMessage ||
    notification ||
    infoNotification;

  const shouldRenderMainContentSection =
    shouldRenderNotificationSection ||
    shouldRenderTextSection ||
    doqType ||
    shouldRenderInputSection;
  const contentShouldStack = modalSize && modalSize === ModalSize.XS;
  if (isLoading) return <ModalLoader />;

  const getAsyncSubmitHandler = (closeCallback?: () => void) => async () => {
    if (!closeCallback) return;
    setIsSubmitting(true);
    await closeCallback();
    // Don't add anything here. At this point component is unmounted!
  };

  return (
    <ModalContainer
      modalSize={modalSize || (doqType ? ModalSize.S : ModalSize.XS)}
      fixedHeight={fixedHeight}
    >
      {shouldRenderHeader && (
        <ModalHeader
          onCloseButtonClick={onCloseButtonClick}
          isCloseButtonDisabled={isInProgress ?? isSubmitting}
          closeButtonLabel={closeButtonLabel}
        >
          {headerText}
        </ModalHeader>
      )}
      <ModalWrapper>
        {shouldRenderMainContentSection && (
          <ModalLayout>
            {shouldRenderNotificationSection && (
              <>
                {notification}
                {errorBlockMessage && (
                  <ErrorNotification>{errorBlockMessage}</ErrorNotification>
                )}
                {warningBlockMessage && (
                  <WarningNotification>
                    {warningBlockMessage}
                  </WarningNotification>
                )}
                {infoNotification && (
                  <InfoNotification>{infoNotification}</InfoNotification>
                )}
              </>
            )}
            {(doqType || shouldRenderTextSection) && (
              <DoqAndTextPositioner shouldStack={Boolean(contentShouldStack)}>
                {doqType && (
                  <DoqWrapper>
                    <DoqFace doqType={doqType} doqSize={DoqSize.SMALL} />
                  </DoqWrapper>
                )}
                {shouldRenderTextSection && (
                  <Text variant="text2">
                    <Stack gap="medium">
                      {subtitle && <ModalSubtitle>{subtitle}</ModalSubtitle>}
                      {Children.toArray(text)
                        .filter(Boolean)
                        .map((textParagraph, index) => (
                          <ModalText key={index}>{textParagraph}</ModalText>
                        ))}
                      {Boolean(listOptions.length) && (
                        <ModalList options={listOptions} />
                      )}
                      {Children.toArray(text2)
                        .filter(Boolean)
                        .map((textParagraph, index) => (
                          <ModalText key={index}>{textParagraph}</ModalText>
                        ))}
                    </Stack>
                  </Text>
                )}
              </DoqAndTextPositioner>
            )}
            {shouldRenderInputSection && (
              <>
                {(inputLabel || onInputValueChange) && (
                  <TextInput
                    autoFocus
                    ref={textInputRef}
                    placeholder={inputPlaceHolderText}
                    id={textInputId}
                    label={inputLabel}
                    value={inputValue}
                    onValueChange={onInputValueChange}
                    isDisabled={isInProgress ?? isSubmitting}
                    onKeyDown={({ key }) =>
                      key === 'Enter' &&
                      getAsyncSubmitHandler(onInputEnterPress)()
                    }
                    dataTestId={inputDataTestId}
                  />
                )}
                {formContent}
              </>
            )}
          </ModalLayout>
        )}
        {children}
        {showRememberActionCheckbox && (
          <ModalLayout>
            <Checkbox
              isChecked={Boolean(rememberActionCheckboxState)}
              onChange={() =>
                onRememberActionCheckboxChange?.(!rememberActionCheckboxState)
              }
            >
              <Paragraph variant="text2">
                {rememberActionCustomCheckboxText ||
                  "Don't show this message again"}
              </Paragraph>
            </Checkbox>
          </ModalLayout>
        )}
      </ModalWrapper>
      {shouldRenderFooter && (
        <ModalFooter>
          {(secondaryButtonText || dangerButtonText || primaryButtonText) && (
            <ButtonContainer
              $modalSize={modalSize || (doqType ? ModalSize.S : ModalSize.XS)}
            >
              {secondaryButtonText && (
                <Button
                  buttonType={
                    dangerButtonText
                      ? ButtonType.TERTIARY
                      : ButtonType.SECONDARY
                  }
                  onClick={onSecondaryButtonClick}
                  isDisabled={
                    isSecondaryButtonDisabled || (isInProgress ?? isSubmitting)
                  }
                  data-click-id={secondaryButtonClickId}
                  dataTestId={secondaryButtonDataTestId}
                >
                  {secondaryButtonText}
                </Button>
              )}
              {dangerButtonText && (
                <DangerButton
                  onClick={getAsyncSubmitHandler(onDangerButtonClick)}
                  isDisabled={isDangerButtonDisabled}
                  isLoading={isInProgress ?? isSubmitting}
                  data-click-id={dangerButtonClickId}
                  dataTestId={dangerButtonDataTestId}
                >
                  {dangerButtonText}
                </DangerButton>
              )}
              {primaryButtonText && (
                <PrimaryButton
                  onClick={getAsyncSubmitHandler(onPrimaryButtonClick)}
                  isDisabled={isPrimaryButtonDisabled}
                  isLoading={isInProgress ?? isSubmitting}
                  data-click-id={primaryButtonClickId}
                  dataTestId={primaryButtonDataTestId}
                  ref={primaryButtonRef}
                >
                  {primaryButtonText}
                </PrimaryButton>
              )}
            </ButtonContainer>
          )}

          {footerContent}
        </ModalFooter>
      )}
    </ModalContainer>
  );
};

export { ModalTemplate };
